import { useEffect } from 'react';

/**
 * Drives the CSS variables the global header reads to push itself below the
 * site-wide top banner:
 *
 * - `--ad-top-banner-height` — the banner's full height. Used by the absolute
 *   hero-overlay desktop header to sit statically below the banner.
 * - `--ad-top-banner-offset` — `max(0, height - scrollY)`, so the fixed mobile
 *   header slides up to the top as the banner scrolls away.
 *
 * Both are set on `targetRef` (the layout root the header descends from) and
 * cleared when inactive, restoring the default `top: 0` behaviour. The in-flow
 * banner already pushes the rest of the page (and the relative desktop header)
 * down on its own.
 */
/**
 * Sanity ceiling on the measured banner height. Real creatives render ≤231px;
 * anything larger is a mis-sized (portrait/square) creative that would strand
 * the fixed header mid-viewport (BC-4699).
 */
const MAX_BANNER_HEIGHT = 300;

export const useTopBannerOffset = (
    bannerRef: React.RefObject<HTMLElement | null>,
    targetRef: React.RefObject<HTMLElement | null>,
    active: boolean,
    /**
     * Identity of the ad being shown. Re-binds the observer when a client-side
     * navigation swaps the creative, so it never keeps measuring a stale node.
     */
    adId?: string,
): void => {
    useEffect(() => {
        const target = targetRef.current;
        const banner = bannerRef.current;

        const clear = () => {
            target?.style.removeProperty('--ad-top-banner-height');
            target?.style.removeProperty('--ad-top-banner-offset');
        };

        if (!active || !target || !banner) {
            clear();
            return;
        }

        let height = 0;
        let frame = 0;

        const applyOffset = () => {
            frame = 0;
            // Content blockers can remove the banner node after measurement;
            // the ResizeObserver dies with it, so without this the header
            // stays offset over empty space forever. Pin the vars to 0 inline
            // rather than clear(): React still renders the slot's scoped SSR
            // defaults (100px/231px), which removal would un-shadow.
            if (!banner.isConnected) {
                target.style.setProperty('--ad-top-banner-height', '0px');
                target.style.setProperty('--ad-top-banner-offset', '0px');
                return;
            }
            // Clamp to [0, height]: scrollY can go negative during iOS rubber-band
            // overscroll, which would otherwise push the header below the banner.
            const offset = Math.min(
                height,
                Math.max(0, height - window.scrollY),
            );
            target.style.setProperty('--ad-top-banner-offset', `${offset}px`);
        };

        const requestApply = () => {
            if (!frame) {
                frame = window.requestAnimationFrame(applyOffset);
            }
        };

        const setHeight = (next: number) => {
            height = Math.min(next, MAX_BANNER_HEIGHT);
            target.style.setProperty('--ad-top-banner-height', `${height}px`);
            applyOffset();
        };

        setHeight(banner.getBoundingClientRect().height);

        const resizeObserver = new ResizeObserver(() => {
            setHeight(banner.getBoundingClientRect().height);
        });
        resizeObserver.observe(banner);
        window.addEventListener('scroll', requestApply, { passive: true });

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('scroll', requestApply);
            if (frame) {
                window.cancelAnimationFrame(frame);
            }
            clear();
        };
    }, [active, adId, bannerRef, targetRef]);
};
